/**
 * Lead Service
 * Lead (potenciális ügyfél) kezelési szolgáltatás - MANUÁLIS FRISSÍTÉSSEL
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { Lead, LeadFormData, LeadComment, LeadConversionData } from '../types/lead';

const LEADS_COLLECTION = 'leads';
const LEAD_COMMENTS_COLLECTION = 'leadComments';

class LeadService {
  /**
   * Lead létrehozása
   */
  async createLead(
    leadData: LeadFormData,
    organizationId: string,
    createdBy?: string,
    createdByName?: string
  ): Promise<string> {
    try {
      const lead: Omit<Lead, 'id'> = {
        ...leadData,
        organizationId,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy,
        createdByName
      };

      const docRef = await addDoc(collection(db, LEADS_COLLECTION), {
        ...lead,
        createdAt: Timestamp.fromDate(lead.createdAt),
        updatedAt: Timestamp.fromDate(lead.updatedAt),
        nextFollowUpAt: lead.nextFollowUpAt ? Timestamp.fromDate(lead.nextFollowUpAt) : null
      });

      return docRef.id;
    } catch (error) {
      console.error('Error creating lead:', error);
      throw error;
    }
  }

  /**
   * Lead frissítése
   */
  async updateLead(
    leadId: string,
    leadData: Partial<LeadFormData>,
    updatedBy?: string
  ): Promise<void> {
    try {
      const docRef = doc(db, LEADS_COLLECTION, leadId);
      
      await updateDoc(docRef, {
        ...leadData,
        updatedAt: Timestamp.now(),
        updatedBy,
        nextFollowUpAt: leadData.nextFollowUpAt ? Timestamp.fromDate(leadData.nextFollowUpAt) : null
      });
    } catch (error) {
      console.error('Error updating lead:', error);
      throw error;
    }
  }

  /**
   * Lead törlése
   */
  async deleteLead(leadId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, LEADS_COLLECTION, leadId));
      
      // Kapcsolódó kommentek törlése
      const commentsQuery = query(
        collection(db, LEAD_COMMENTS_COLLECTION),
        where('leadId', '==', leadId)
      );
      const commentsSnapshot = await getDocs(commentsQuery);
      
      const batch = writeBatch(db);
      commentsSnapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
      });
      await batch.commit();
    } catch (error) {
      console.error('Error deleting lead:', error);
      throw error;
    }
  }

  /**
   * Lead lekérése ID alapján
   */
  async getLead(leadId: string): Promise<Lead | null> {
    try {
      const docRef = doc(db, LEADS_COLLECTION, leadId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        nextFollowUpAt: data.nextFollowUpAt?.toDate(),
        lastContactedAt: data.lastContactedAt?.toDate(),
        convertedAt: data.convertedAt?.toDate()
      } as Lead;
    } catch (error) {
      console.error('Error fetching lead:', error);
      return null;
    }
  }

  /**
   * Összes lead lekérése (MANUÁLIS - nincs real-time listener)
   */
  async getLeads(organizationId: string): Promise<Lead[]> {
    try {
      const q = query(
        collection(db, LEADS_COLLECTION),
        where('organizationId', '==', organizationId),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);

      return snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          nextFollowUpAt: data.nextFollowUpAt?.toDate(),
          lastContactedAt: data.lastContactedAt?.toDate(),
          convertedAt: data.convertedAt?.toDate()
        } as Lead;
      });
    } catch (error) {
      console.error('Error fetching leads:', error);
      return [];
    }
  }

  /**
   * Lead státusz frissítése
   */
  async updateLeadStatus(
    leadId: string,
    status: Lead['status'],
    updatedBy?: string
  ): Promise<void> {
    try {
      const docRef = doc(db, LEADS_COLLECTION, leadId);
      
      const updateData: any = {
        status,
        updatedAt: Timestamp.now(),
        updatedBy
      };

      // Ha contacted státuszra váltunk, frissítjük a lastContactedAt-ot
      if (status === 'contacted') {
        updateData.lastContactedAt = Timestamp.now();
      }

      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating lead status:', error);
      throw error;
    }
  }

  /**
   * Lead konvertálása ügyfélre
   */
  async convertLeadToCustomer(
    conversionData: LeadConversionData,
    convertedBy: string
  ): Promise<string> {
    try {
      const { leadId, customerData } = conversionData;

      // Ügyfél létrehozása
      const customerRef = await addDoc(collection(db, 'customers'), {
        ...customerData,
        organizationId: (await this.getLead(leadId))?.organizationId,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: convertedBy,
        source: 'lead_conversion'
      });

      // Lead frissítése
      await updateDoc(doc(db, LEADS_COLLECTION, leadId), {
        status: 'converted',
        convertedToCustomerId: customerRef.id,
        convertedAt: Timestamp.now(),
        convertedBy,
        updatedAt: Timestamp.now()
      });

      return customerRef.id;
    } catch (error) {
      console.error('Error converting lead to customer:', error);
      throw error;
    }
  }

  /**
   * Komment hozzáadása
   */
  async addComment(
    leadId: string,
    organizationId: string,
    content: string,
    createdBy: string,
    createdByName: string,
    isInternal: boolean = false
  ): Promise<string> {
    try {
      const comment: Omit<LeadComment, 'id'> = {
        leadId,
        organizationId,
        content,
        createdBy,
        createdByName,
        createdAt: new Date(),
        isInternal
      };

      const docRef = await addDoc(collection(db, LEAD_COMMENTS_COLLECTION), {
        ...comment,
        createdAt: Timestamp.fromDate(comment.createdAt)
      });

      return docRef.id;
    } catch (error) {
      console.error('Error adding comment:', error);
      throw error;
    }
  }

  /**
   * Kommentek lekérése (MANUÁLIS)
   */
  async getComments(leadId: string): Promise<LeadComment[]> {
    try {
      const q = query(
        collection(db, LEAD_COMMENTS_COLLECTION),
        where('leadId', '==', leadId),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);

      return snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate()
        } as LeadComment;
      });
    } catch (error) {
      console.error('Error fetching comments:', error);
      return [];
    }
  }

  /**
   * Komment törlése
   */
  async deleteComment(commentId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, LEAD_COMMENTS_COLLECTION, commentId));
    } catch (error) {
      console.error('Error deleting comment:', error);
      throw error;
    }
  }

  /**
   * Lead scoring frissítése
   */
  async updateLeadScore(leadId: string, score: number): Promise<void> {
    try {
      await updateDoc(doc(db, LEADS_COLLECTION, leadId), {
        score,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating lead score:', error);
      throw error;
    }
  }

  /**
   * Lead hozzárendelése felhasználóhoz
   */
  async assignLead(
    leadId: string,
    assignedTo: string,
    assignedToName: string
  ): Promise<void> {
    try {
      await updateDoc(doc(db, LEADS_COLLECTION, leadId), {
        assignedTo,
        assignedToName,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error assigning lead:', error);
      throw error;
    }
  }
}

export const leadService = new LeadService();
